import React, { useState, useEffect } from 'react';
import { FiPlus, FiSearch, FiEdit2, FiTrash2, FiImage, FiFilter, FiUpload, FiX, FiSave } from 'react-icons/fi';
import axios from '../config/axios';
import { useTranslation } from '../hooks/useTranslation';

const ChallengeGallery = () => {
  const { t } = useTranslation();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [filters, setFilters] = useState({
    category: 'all',
    search: ''
  });
  const [stats, setStats] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Challenge-specific categories
  const categories = [
    { value: 'nature', label: 'Nature', color: 'bg-green-100 text-green-800' },
    { value: 'urban', label: 'Urban', color: 'bg-blue-100 text-blue-800' },
    { value: 'historic', label: 'Historic', color: 'bg-amber-100 text-amber-800' },
    { value: 'adventure', label: 'Adventure', color: 'bg-red-100 text-red-800' },
    { value: 'water', label: 'Water', color: 'bg-cyan-100 text-cyan-800' },
    { value: 'park', label: 'Park', color: 'bg-emerald-100 text-emerald-800' }
  ];

  useEffect(() => {
    loadImages();
    loadStats();
  }, [filters]);

  const loadImages = async () => {
    try {
      const params = {};
      if (filters.category !== 'all') params.category = filters.category;
      if (filters.search) params.search = filters.search;

      const response = await axios.get('/cms/challenge-gallery', { params });
      setImages(response.data);
    } catch (error) {
      console.error('Error loading challenge gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get('/cms/challenge-gallery/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const confirmDelete = async () => {
    if (!selectedImage) return;

    setDeleting(true);
    try {
      await axios.delete(`/cms/challenge-gallery/${selectedImage.id}`);
      setImages(prev => prev.filter(img => img.id !== selectedImage.id));
      loadStats();
      setDeleteModalOpen(false);
      setSelectedImage(null);
    } catch (error) {
      console.error('Error deleting image:', error);
    } finally {
      setDeleting(false);
    }
  };

  const getCategoryColor = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat ? cat.color : 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary-600">{t('cms.challengeGallery.title')}</h1>
          <p className="text-gray-600">{t('cms.challengeGallery.subtitle')}</p>
        </div>
        <button
          onClick={() => setUploadModalOpen(true)}
          className="btn btn-primary flex items-center gap-2"
        >
          <FiPlus className="w-4 h-4" />
          Upload Images
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Images</p>
                <p className="text-2xl font-bold text-gray-900">{stats.stats.total_images}</p>
              </div>
              <FiImage className="w-8 h-8 text-primary-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Gallery Uploads</p>
                <p className="text-2xl font-bold text-gray-900">{stats.stats.gallery_uploads || 0}</p>
              </div>
              <FiUpload className="w-8 h-8 text-green-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">From Challenges</p>
                <p className="text-2xl font-bold text-gray-900">{stats.stats.from_challenges || 0}</p>
              </div>
              <FiFilter className="w-8 h-8 text-blue-500" />
            </div>
          </div>
        </div>
      )}

      {/* Category breakdown */}
      {stats && stats.categories && stats.categories.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Images by Category</h3>
          <div className="flex flex-wrap gap-2">
            {stats.categories.map(cat => (
              <span
                key={cat.category}
                className={`px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(cat.category)}`}
              >
                {cat.category}: {cat.count}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder={t('cms.common.search')}
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Category Filter */}
          <div className="flex items-center gap-2">
            <FiFilter className="w-4 h-4 text-gray-500" />
            <select
              value={filters.category}
              onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>

          {/* Results Count */}
          <div className="text-sm text-gray-600">
            {images.length} image{images.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Images Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600 mt-4">Loading gallery...</p>
        </div>
      ) : images.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
          <FiImage className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">No challenge images found</p>
          <button
            onClick={() => setUploadModalOpen(true)}
            className="inline-flex items-center gap-2 btn btn-primary"
          >
            <FiPlus className="w-4 h-4" />
            Upload Your First Images
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {images.map(image => (
            <div key={image.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow group">
              {/* Image - clickable for lightbox */}
              <div
                className="relative aspect-square bg-gray-100 cursor-pointer"
                onClick={() => {
                  setSelectedImage(image);
                  setViewModalOpen(true);
                }}
              >
                <img
                  src={`${process.env.REACT_APP_API_URL.replace('/api', '')}${image.thumbnail_url || image.image_url}`}
                  alt={image.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                {/* Source badge */}
                <div className={`absolute top-2 left-2 px-2 py-0.5 rounded text-xs font-medium ${
                  image.source === 'gallery'
                    ? 'bg-green-500 text-white'
                    : 'bg-blue-500 text-white'
                }`}>
                  {image.source === 'gallery' ? 'Uploaded' : 'From Challenge'}
                </div>
                {/* Overlay on hover - only for gallery uploads */}
                {image.source === 'gallery' && (
                  <div
                    className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={() => {
                        setSelectedImage(image);
                        setEditModalOpen(true);
                      }}
                      className="p-2 bg-white text-primary-500 rounded-lg hover:bg-primary-50 transition-colors"
                    >
                      <FiEdit2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedImage(image);
                        setDeleteModalOpen(true);
                      }}
                      className="p-2 bg-white text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                    >
                      <FiTrash2 className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="p-3">
                <h3 className="font-semibold text-gray-900 text-sm line-clamp-1 mb-1">{image.title}</h3>
                {image.description && (
                  <p className="text-xs text-gray-600 line-clamp-2 mb-2">{image.description}</p>
                )}
                <div className="flex items-center justify-between">
                  <span className={`px-2 py-0.5 text-xs font-medium rounded capitalize ${getCategoryColor(image.category)}`}>
                    {image.category}
                  </span>
                  <span className="text-xs text-gray-500">
                    {new Date(image.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      {uploadModalOpen && (
        <UploadModal
          onClose={() => setUploadModalOpen(false)}
          onSuccess={() => {
            loadImages();
            loadStats();
            setUploadModalOpen(false);
          }}
          categories={categories}
        />
      )}

      {/* Edit Modal */}
      {editModalOpen && selectedImage && (
        <EditModal
          image={selectedImage}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedImage(null);
          }}
          onSuccess={() => {
            loadImages();
            loadStats();
            setEditModalOpen(false);
            setSelectedImage(null);
          }}
          categories={categories}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && selectedImage && (
        <DeleteConfirmModal
          image={selectedImage}
          onClose={() => {
            setDeleteModalOpen(false);
            setSelectedImage(null);
          }}
          onConfirm={confirmDelete}
          deleting={deleting}
        />
      )}

      {/* Lightbox Modal */}
      {viewModalOpen && selectedImage && (
        <ImageLightbox
          image={selectedImage}
          onClose={() => {
            setViewModalOpen(false);
            setSelectedImage(null);
          }}
        />
      )}
    </div>
  );
};

// Upload Modal Component with batch support
const UploadModal = ({ onClose, onSuccess, categories }) => {
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, currentFile: '' });
  const [defaultCategory, setDefaultCategory] = useState('nature');

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const filesWithPreview = files.map(file => ({
        file,
        preview: URL.createObjectURL(file),
        title: file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' '),
        description: '',
        category: defaultCategory,
        displayOrder: 0
      }));
      setSelectedFiles(prev => [...prev, ...filesWithPreview]);
    }
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => {
      const newFiles = prev.filter((_, i) => i !== index);
      URL.revokeObjectURL(prev[index].preview);
      return newFiles;
    });
  };

  const updateFileData = (index, field, value) => {
    setSelectedFiles(prev => prev.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    ));
  };

  const applyToAll = (field, value) => {
    setSelectedFiles(prev => prev.map(item => ({ ...item, [field]: value })));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedFiles.length === 0) {
      return;
    }

    setUploading(true);
    setUploadProgress({ current: 0, total: selectedFiles.length, currentFile: '' });

    try {
      let successCount = 0;
      let failCount = 0;

      for (let i = 0; i < selectedFiles.length; i++) {
        const fileData = selectedFiles[i];
        setUploadProgress({
          current: i + 1,
          total: selectedFiles.length,
          currentFile: fileData.title
        });

        try {
          const data = new FormData();
          data.append('image', fileData.file);
          data.append('title', fileData.title);
          data.append('description', fileData.description);
          data.append('category', fileData.category);
          data.append('displayOrder', fileData.displayOrder);

          await axios.post('/cms/challenge-gallery', data, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 120000
          });

          successCount++;
        } catch (error) {
          console.error(`Error uploading ${fileData.title}:`, error);
          failCount++;
        }
      }

      if (successCount > 0) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error during batch upload:', error);
    } finally {
      setUploading(false);
      setUploadProgress({ current: 0, total: 0, currentFile: '' });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Upload Challenge Images</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <FiX className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Default category for new uploads */}
          <div className="bg-gray-50 rounded-lg p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Default Category for New Uploads</label>
            <select
              value={defaultCategory}
              onChange={(e) => setDefaultCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
            {selectedFiles.length > 0 && (
              <button
                type="button"
                onClick={() => applyToAll('category', defaultCategory)}
                className="ml-3 text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Apply to all
              </button>
            )}
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Images *</label>
            {selectedFiles.length > 0 ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <span className="text-sm font-medium text-blue-900">
                    {selectedFiles.length} image{selectedFiles.length !== 1 ? 's' : ''} selected
                  </span>
                  <button
                    type="button"
                    onClick={() => setSelectedFiles([])}
                    className="text-sm text-blue-700 hover:text-blue-900 font-medium"
                  >
                    Clear All
                  </button>
                </div>

                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {selectedFiles.map((fileData, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex gap-3">
                        {/* Preview */}
                        <img
                          src={fileData.preview}
                          alt="Preview"
                          className="w-20 h-20 object-cover rounded flex-shrink-0"
                        />

                        {/* Details */}
                        <div className="flex-1 space-y-2">
                          <input
                            type="text"
                            value={fileData.title}
                            onChange={(e) => updateFileData(index, 'title', e.target.value)}
                            placeholder="Image title..."
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                          />
                          <input
                            type="text"
                            value={fileData.description}
                            onChange={(e) => updateFileData(index, 'description', e.target.value)}
                            placeholder="Optional description..."
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                          />
                          <select
                            value={fileData.category}
                            onChange={(e) => updateFileData(index, 'category', e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                          >
                            {categories.map(cat => (
                              <option key={cat.value} value={cat.value}>{cat.label}</option>
                            ))}
                          </select>
                        </div>

                        {/* Remove Button */}
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="flex-shrink-0 p-2 text-red-600 hover:bg-red-50 rounded"
                        >
                          <FiX className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <label className="cursor-pointer">
                  <div className="flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 transition-colors">
                    <FiPlus className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">Add more images</span>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleFileSelect}
                  />
                </label>
              </div>
            ) : (
              <label className="cursor-pointer">
                <div className="flex flex-col items-center justify-center gap-2 px-4 py-12 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 transition-colors">
                  <FiUpload className="w-8 h-8 text-gray-400" />
                  <span className="text-gray-600">Click to upload images</span>
                  <span className="text-xs text-gray-500">Select one or multiple images (JPEG, PNG, GIF, WebP)</span>
                  <span className="text-xs text-gray-500">Max 10MB per file</span>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleFileSelect}
                />
              </label>
            )}
          </div>

          {/* Upload Progress */}
          {uploading && uploadProgress.total > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-blue-900">
                  Uploading {uploadProgress.current} of {uploadProgress.total}
                </span>
                <span className="text-xs text-blue-700">
                  {Math.round((uploadProgress.current / uploadProgress.total) * 100)}%
                </span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2 mb-2">
                <div
                  className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                ></div>
              </div>
              {uploadProgress.currentFile && (
                <p className="text-xs text-blue-700">Current: {uploadProgress.currentFile}</p>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={uploading}
              className="flex-1 px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading || selectedFiles.length === 0}
              className="btn btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <FiUpload className="w-4 h-4" />
              {uploading
                ? `Uploading ${uploadProgress.current}/${uploadProgress.total}...`
                : `Upload ${selectedFiles.length} Image${selectedFiles.length !== 1 ? 's' : ''}`
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Modal Component
const EditModal = ({ image, onClose, onSuccess, categories }) => {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: image.title,
    description: image.description || '',
    category: image.category,
    displayOrder: image.displayOrder
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    setSaving(true);
    try {
      await axios.put(`/cms/challenge-gallery/${image.id}`, formData);
      onSuccess();
    } catch (error) {
      console.error('Error updating image:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Edit Image</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <FiX className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Preview */}
          <div>
            <img
              src={`${process.env.REACT_APP_API_URL.replace('/api', '')}${image.image_url}`}
              alt={image.title}
              className="w-full h-64 object-cover rounded-lg"
            />
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>

            {/* Display Order */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Display Order</label>
              <input
                type="number"
                value={formData.displayOrder}
                onChange={(e) => setFormData(prev => ({ ...prev, displayOrder: parseInt(e.target.value) }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <FiSave className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Delete Confirmation Modal Component
const DeleteConfirmModal = ({ image, onClose, onConfirm, deleting }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
            <FiTrash2 className="w-6 h-6 text-red-600" />
          </div>

          <h2 className="text-xl font-bold text-gray-900 text-center mb-2">Delete Image?</h2>
          <p className="text-gray-600 text-center mb-4">
            Are you sure you want to delete "<strong>{image.title}</strong>"? This action cannot be undone.
          </p>

          {/* Image Preview */}
          <div className="mb-4">
            <img
              src={`${process.env.REACT_APP_API_URL.replace('/api', '')}${image.thumbnail_url || image.image_url}`}
              alt={image.title}
              className="w-full h-32 object-cover rounded-lg"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={deleting}
              className="flex-1 px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={deleting}
              className="flex-1 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {deleting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Deleting...
                </>
              ) : (
                <>
                  <FiTrash2 className="w-4 h-4" />
                  Delete
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Image Lightbox Component
const ImageLightbox = ({ image, onClose }) => {
  const [loading, setLoading] = useState(true);

  // Close on ESC key
  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
      onClick={onClose}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full transition-colors"
      >
        <FiX className="w-6 h-6 text-white" />
      </button>

      {/* Image container */}
      <div
        className="max-w-[90vw] max-h-[90vh] relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Loading spinner */}
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {/* Full size image */}
        <img
          src={`${process.env.REACT_APP_API_URL.replace('/api', '')}${image.image_url}`}
          alt={image.title}
          className={`max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl transition-opacity duration-300 ${loading ? 'opacity-0' : 'opacity-100'}`}
          onLoad={() => setLoading(false)}
        />

        {/* Image info */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4 rounded-b-lg">
          <h3 className="text-white font-semibold text-lg">{image.title}</h3>
          {image.description && (
            <p className="text-gray-300 text-sm mt-1">{image.description}</p>
          )}
          <div className="flex items-center gap-3 mt-2">
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
              image.source === 'gallery'
                ? 'bg-green-500 text-white'
                : 'bg-blue-500 text-white'
            }`}>
              {image.source === 'gallery' ? 'Uploaded' : 'From Challenge'}
            </span>
            <span className="text-gray-400 text-xs capitalize">{image.category}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChallengeGallery;
